import { ENVIRONMENT } from 'server/constants/environment';
import type { IBuildUrls } from 'server/helpers/getBuildUrls';
import type { IUser } from 'server/helpers/getUser';
import type { IOfficeOnline } from 'server/types/context/IOfficeOnline';
import { EEnvironment } from 'server/types/EEnvironment';

export interface IEditorConfig {
    title: string;
    id: EEditors;
    default?: boolean;
    description: string;
    icon: Record<string, string>;
    'x-docs-root'?: string;
    view_url?: string;
    edit_uri?: string;
    create_uri?: string;
    edit_storage?: string;
    edit_data?: (isEdit: boolean, id: string) => Partial<IOfficeOnline> | undefined;
    documentsRoot: string;
    wopi?: boolean;
    ['x-feedback-url']?: string;
}

export enum EEditors {
    MAILPAD = 'MailPad',
    OFFICE = 'office',
    R7 = 'R7',
    MYOFFICE = 'myoffice',
    MYOFFICE_ATTACHES = 'myofficeAttaches',
    MYOFFICE_TOUCH = 'myoffice-touch',
}

export enum EMode {
    EDIT = 'edit',
    VIEW = 'view',
}

export const getEditorsConfig = (editorName: EEditors, buildUrls: IBuildUrls, user: IUser): IEditorConfig | undefined => {
    if (editorName === EEditors.MAILPAD) {
        const documentsRoot = ENVIRONMENT === EEnvironment.DEV ? '//docs.win54.dev.mail.ru/' : '//docs.mail.ru/';

        return {
            id: EEditors.MAILPAD,
            title: 'Редактор Mail.ru',
            description: 'Новое приложение Mail.ru для редактирования документов Word. Доступна альфа-версия с базовым функционалом',
            icon: {
                '16x16': `${buildUrls.img}editor/mailpad_16x16.png`,
                '32x32': `${buildUrls.img}editor/mailpad_32x32.png`,
            },
            'x-docs-root': documentsRoot,
            documentsRoot,
        };
    }

    if (editorName === EEditors.R7) {
        return {
            id: EEditors.R7,
            title: 'Редактор R7',
            description: 'Редактор R7',
            view_url: '/r7/view/',
            edit_uri: '/r-seven/edit/',
            create_uri: '/r-seven/create/',
            edit_storage: 'r-seven/edit/home',
            edit_data(isEdit, id) {
                return {
                    access_token_ttl: 0,
                    Status: '200',
                    edit_url: `/${id}`,
                    action: isEdit ? EMode.EDIT : EMode.VIEW,
                    access_token: 'fakeForR7',
                };
            },
            icon: {
                '16x16': `${buildUrls.img}editor/hancom_16x16.png`,
                '32x32': `${buildUrls.img}editor/hancom_32x32.png`,
            },
            documentsRoot: '/r7/view/',
        };
    }

    if (editorName === EEditors.MYOFFICE || editorName === EEditors.MYOFFICE_TOUCH) {
        return {
            id: EEditors.MYOFFICE,
            title: 'Редактор Мой офис',
            description: 'Редактор Мой офис',
            wopi: true,
            view_url: '/myoffice/view/',
            edit_uri: '/myoffice/edit/',
            create_uri: '/myoffice/',
            edit_storage: 'myoffice/edit/home',
            edit_data(isEdit, id) {
                return {
                    action: isEdit ? EMode.EDIT : EMode.VIEW,
                    edit_url: `/${id}`,
                };
            },
            icon: {
                '16x16': `${buildUrls.img}editor/hancom_16x16.png`,
                '32x32': `${buildUrls.img}editor/hancom_32x32.png`,
            },
            documentsRoot: '',
        };
    }

    if (editorName === EEditors.MYOFFICE_ATTACHES) {
        return {
            id: EEditors.MYOFFICE_ATTACHES,
            title: 'Редактор Мой офис',
            description: 'Редактор Мой офис',
            wopi: true,
            view_url: '/myoffice/view/',
            edit_uri: '/myoffice/edit/',
            create_uri: '/myoffice/',
            edit_storage: 'myoffice/edit/attaches',
            edit_data(isEdit, id) {
                return {
                    action: isEdit ? EMode.EDIT : EMode.VIEW,
                    edit_url: `/${id}`,
                };
            },
            icon: {
                '16x16': `${buildUrls.img}editor/hancom_16x16.png`,
                '32x32': `${buildUrls.img}editor/hancom_32x32.png`,
            },
            documentsRoot: '/myoffice/view/',
            'x-feedback-url': 'https://help.mail.ru/biz/support',
        };
    }

    if (editorName === EEditors.OFFICE) {
        return {
            id: EEditors.OFFICE,
            title: 'Microsoft Office Online',
            description: 'Онлайн-версия Microsoft Office, позволяющая редактировать документы Word, таблицы Excel и презентации PowerPoint',
            icon: {
                '16x16': `${buildUrls.img}editor/owa_16x16.png`,
                '32x32': `${buildUrls.img}editor/owa_32x32.png`,
            },
            edit_uri:
                user.isMailUser || user.isBizUser || user.externalUser || ENVIRONMENT === EEnvironment.DEV ? '/office/edit/' : undefined,
            edit_storage: 'office/edit/home',
            edit_data: (isEdit, id) => {
                return {
                    Status: '200',
                    edit_url: `/${id}`,
                    action: isEdit ? 'edit' : 'view',
                };
            },
            documentsRoot: '/office/view',
        };
    }
};
